import React, {useState, useCallback} from "react";
import {FiAlertTriangle, FiRefreshCw, FiMail, FiCopy, FiCheck, FiX} from "react-icons/fi";
import Swal from "sweetalert2";
import {JobRead, JobStatus, JobType, JobWarning, getJobStatusLabel} from "../../schemas";
import {ThemeColors} from "../../colors";
import {reprocessDocuments, dismissJobWarnings} from "../../api";
import {useJobContext} from "../../contexts/job_context";
import {useGroupContext} from "../../contexts/group_context";

interface JobProgressListProps {
  jobs: JobRead[];
  maxHeight?: string;
  compact?: boolean;
}

const clampPercent = (p: number): number => Math.max(0, Math.min(100, p));

const SUPPORT_EMAIL = "ceyer@loa-ai.com";

// ── Warning Details (inline, no nested card) ────────────────────────────

const WarningDetails: React.FC<{ warnings: JobWarning[]; jobId: number }> = ({ warnings, jobId }) => {
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  const handleCopy = useCallback((e: React.MouseEvent, idx: number, w: JobWarning) => {
    e.preventDefault();
    e.stopPropagation();
    const text = `File: ${w.file_name}\nJob ID: ${jobId}\nType: ${w.error_type}\nDetails: ${w.message}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    });
  }, []);

  return (
    <div
      className="space-y-2 mt-1"
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
    >
      {warnings.map((w, i) => {
        const mailBody = `File: ${w.file_name}\nJob ID: ${jobId}\nType: ${w.error_type}\nDetails: ${w.message}`;
        const mailHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`File issue: ${w.file_name}`)}&body=${encodeURIComponent(mailBody)}`;
        return (
          <div key={i}>
            <p className="text-[11px] text-slate-700">
              <span className="font-semibold">{w.file_name}</span>
              {' — '}
              <span className="text-slate-500">{w.message}</span>
            </p>
            <div className="flex items-center gap-1.5 mt-1">
              <button
                type="button"
                onClick={e => handleCopy(e, i, w)}
                onMouseDown={e => e.stopPropagation()}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-slate-500 hover:text-slate-700 hover:bg-black/5 transition-colors cursor-pointer"
              >
                {copiedIdx === i
                  ? <><FiCheck className="w-2.5 h-2.5 text-green-600"/><span className="text-green-600">Copied</span></>
                  : <><FiCopy className="w-2.5 h-2.5"/><span>Copy</span></>
                }
              </button>
              <a
                href={mailHref}
                onClick={e => e.stopPropagation()}
                onMouseDown={e => e.stopPropagation()}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-colors cursor-pointer no-underline"
              >
                <FiMail className="w-2.5 h-2.5"/>
                <span>Email support</span>
              </a>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Job Progress Item ───────────────────────────────────────────────────

const JobProgressItem: React.FC<{ job: JobRead; compact?: boolean }> = ({ job, compact = false }) => {
  const [retrying, setRetrying] = useState(false);
  const {dismissJob, addJobs} = useJobContext()!;
  const groupCtx = useGroupContext();
  const percent = clampPercent((job.progress ?? 0) * 100);
  const label = getJobStatusLabel(job);
  const isFailed = job.status === JobStatus.FAILED;

  const handleRetry = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!job.updating_ids?.length) return;
    setRetrying(true);
    try {
      const result = await reprocessDocuments(job.updating_ids);
      const newJob: JobRead = result.job ?? {
        id: result.job_id,
        type: JobType.UPLOAD,
        status: JobStatus.PROCESSING,
        progress: 0,
        updating_ids: job.updating_ids,
        updated_ids: [],
        folder_ids: job.folder_ids ?? [],
        group_id: job.group_id,
      };
      dismissJob(job.id);
      addJobs([newJob]);
    } catch (err) {
      console.error("Reprocess failed:", err);
    } finally {
      setRetrying(false);
    }
  };

  const warnings = job.warnings ?? [];
  const hasWarnings = warnings.length > 0;

  const handleDismiss = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const fileList = warnings.map(w => `<li><strong>${w.file_name}</strong></li>`).join('');
    const result = await Swal.fire({
      title: 'Dismiss &amp; Delete?',
      html: `<p style="margin-bottom:8px">The following file${warnings.length > 1 ? 's' : ''} will be permanently deleted:</p><ul style="text-align:left;margin:0 auto;max-width:320px">${fileList}</ul><p style="margin-top:8px;font-size:13px;color:#6b7280">Empty folders will also be removed.</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: ThemeColors.neutralDarkHex,
      cancelButtonColor: ThemeColors.colorContrastLightHex,
      confirmButtonText: 'Delete &amp; Dismiss',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    Swal.fire({title: 'Deleting...', text: 'Removing files and cleaning up.', allowOutsideClick: false, didOpen: () => Swal.showLoading()});
    try {
      await dismissJobWarnings(job.id);
      const isTerminal = job.status === JobStatus.COMPLETED || job.status === JobStatus.FAILED;
      if (isTerminal) {
        // Job is done — remove from active list permanently
        dismissJob(job.id);
      }
      // else: job still processing — next poll brings it back without warnings
      groupCtx?.refreshCurrentGroup();
      Swal.fire({title: 'Dismissed', text: 'Files deleted and warning cleared.', icon: 'success', timer: 2000, showConfirmButton: false});
    } catch (err) {
      console.error("Dismiss failed:", err);
      Swal.fire('Error', 'Failed to dismiss warning. Please try again.', 'error');
    }
  }, [job.id, warnings, dismissJob, groupCtx]);

  // ── Warning block (reused across all job states) ───────────────────────
  const warningBlock = hasWarnings ? (
    <div
      className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 mt-1"
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
    >
      <div className="flex items-center gap-2">
        <FiAlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0"/>
        <span className="text-xs font-medium text-amber-800 flex-1">
          {warnings.length} file{warnings.length > 1 ? 's' : ''} had issues
        </span>
        <button
          onClick={handleDismiss}
          onMouseDown={e => e.stopPropagation()}
          className="p-0.5 rounded text-amber-400 hover:text-amber-700 hover:bg-amber-100 transition-colors cursor-pointer"
          title="Dismiss"
        >
          <FiX className="w-3.5 h-3.5"/>
        </button>
      </div>
      <WarningDetails warnings={warnings} jobId={job.id}/>
    </div>
  ) : null;

  // ── Completed — only warnings remain ───────────────────────────────────
  if (job.status === JobStatus.COMPLETED && hasWarnings) {
    return warningBlock;
  }

  // ── Failed ──────────────────────────────────────────────────────────
  if (isFailed) {
    return (
      <div
        className={`rounded-lg bg-red-50 border border-red-200 px-3 ${compact ? 'py-2' : 'py-2.5'}`}
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <FiAlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0"/>
          <span className="text-xs font-medium text-red-700 flex-1">{label}</span>
          <button
            onClick={handleDismiss}
            onMouseDown={e => e.stopPropagation()}
            className="p-0.5 rounded text-red-300 hover:text-red-600 hover:bg-red-100 transition-colors cursor-pointer"
            title="Dismiss"
          >
            <FiX className="w-3.5 h-3.5"/>
          </button>
        </div>
        {warnings.length > 0 && <WarningDetails warnings={warnings} jobId={job.id}/>}
        {job.type === JobType.UPLOAD && job.updating_ids?.length && (
          <button
            onClick={handleRetry}
            onMouseDown={e => e.stopPropagation()}
            disabled={retrying}
            className={`flex items-center justify-center gap-1.5 w-full mt-2 rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50 cursor-pointer ${retrying ? '' : 'shadow-sm'}`}
          >
            <FiRefreshCw className={`w-3.5 h-3.5 ${retrying ? 'animate-spin' : ''}`}/>
            {retrying ? 'Retrying...' : 'Retry'}
          </button>
        )}
      </div>
    );
  }

  // ── Compact progress ────────────────────────────────────────────────
  if (compact) {
    return (
      <div className="py-1">
        <div className="flex items-center gap-2">
          <div className="relative h-1.5 flex-1 rounded-full bg-slate-200 overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out"
              style={{
                width: `${percent}%`,
                background: `linear-gradient(90deg, ${ThemeColors.colorMidHex}, ${ThemeColors.colorContrastLightHex})`,
              }}
            >
              {!isFailed && percent < 100 && (
                <div className="absolute inset-0 animate-shimmer"
                  style={{background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)', width: '30%'}}
                />
              )}
            </div>
          </div>
          <span className="text-[10px] text-slate-500 whitespace-nowrap">{label}</span>
        </div>
        {warningBlock}
      </div>
    );
  }

  // ── Normal progress ─────────────────────────────────────────────────
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <div className="flex items-center gap-3">
        <div className="relative h-2 flex-1 rounded-full bg-slate-200 overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out"
            style={{
              width: `${percent}%`,
              background: `linear-gradient(90deg, ${ThemeColors.colorMidHex}, ${ThemeColors.colorContrastLightHex})`,
            }}
          />
        </div>
        <span className="text-xs text-slate-600 font-medium whitespace-nowrap min-w-[80px] text-right">
          {label}
        </span>
      </div>
      {warningBlock}
    </div>
  );
};

// ── Job Progress List ───────────────────────────────────────────────────

const JobProgressList: React.FC<JobProgressListProps> = ({ jobs, maxHeight, compact = false }) => {
  if (!jobs || jobs.length === 0) {
    return null;
  }

  return (
    <div
      className={[
        "flex flex-col gap-1",
        "scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent overflow-auto",
        maxHeight || "max-h-40",
      ].join(" ")}
    >
      {jobs.map((job) => (
        <JobProgressItem key={job.id} job={job} compact={compact} />
      ))}
    </div>
  );
};

export default JobProgressList;
